<template>
  <div>
    <!-- Header Row -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h5 class="m-0 font-weight-bold">Customer Notes</h5>

      <div class="d-flex align-items-center">
        <b-form-select v-model="type" :options="options" class="w-75 mr-1" />
        <b-form-input
          v-model="search"
          placeholder="Search Notes..."
          class="search-input mr-1"
        ></b-form-input>

        <b-button variant="primary" class="text-nowrap" @click="openModal">
          Add Note
        </b-button>
      </div>
    </div>

    <b-table
      :items="notes"
      :fields="fields"
      responsive
      :busy="loadingNotes"
      show-empty
      thead-class="text-nowrap"
      :tbody-tr-class="rowClass"
    >
      <template #cell(type)="{ item }">
        {{ item.type || "-" }}
      </template>
      <template #cell(date)="{ item }">
        <span class="text-center">{{
          item.date ? format(new Date(item.date), "dd/MM/yyyy") : "-"
        }}</span>
      </template>
      <template #cell(dateAmend)="{ item }">
        {{
          item.dateAmend ? format(new Date(item.dateAmend), "dd/MM/yyyy") : "-"
        }}
      </template>
      <template #cell(note)="{ item }">
        {{ item.note }}
      </template>
      <template #cell(addedBy)="{ item }">
        {{ item.firstName }} {{ item.lastName }}
      </template>
      <template #cell(action)="{ item }">
        <div class="d-flex align-items-center">
          <b-button variant="link" class="p-0" @click="openEditNote(item)">
            <feather-icon icon="EditIcon" />
          </b-button>
        </div>
      </template>
    </b-table>

    <div
      v-if="paginationNotes.total > 0"
      class="d-flex align-items-center justify-content-between mt-1"
    >
      <div class="text-muted small">{{ paginationSummary }}</div>
      <b-col
        cols="12"
        md="6"
        class="d-flex align-items-center justify-content-center justify-content-sm-end"
      >
        <b-pagination
          v-model="page"
          :total-rows="paginationNotes.total"
          :per-page="paginationNotes.limit"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #first-text
            ><span class="text-success">First</span></template
          >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
          <template #last-text><span class="text-info">Last</span></template>
        </b-pagination>
      </b-col>
    </div>

    <b-modal
      id="add-note-modal"
      hide-footer
      centered
      title="Add Note"
      @ok="handleAddNote"
      header-bg-variant="primary"
      @hidden="reset"
    >
      <validation-observer ref="addNote">
        <b-form-group label="Type" label-class="font-weight-bold text-black">
          <b-form-select v-model="form.type" :options="options" />
        </b-form-group>
        <b-form-group label="Date" label-class="font-weight-bold text-black">
          <validation-provider
            #default="{ errors }"
            name="due date"
            rules="required"
          >
            <flat-pickr v-model="form.date" :config="configDate" />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group label="To Do" label-class="font-weight-bold text-black">
          <validation-provider
            #default="{ errors }"
            name="note"
            rules="required"
          >
            <b-form-textarea
              rows="3"
              v-model="form.note"
              placeholder="Enter note..."
            ></b-form-textarea>
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
      </validation-observer>

      <!-- Save button -->
      <div class="text-right mt-1">
        <b-button variant="success" @click="handleAddNote"> Save </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import {
  BTable,
  BPagination,
  BFormInput,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BBadge,
  BFormSelect,
  BFormTextarea,
  BCol,
} from "bootstrap-vue";
import { format } from "date-fns";
import flatPickr from "vue-flatpickr-component";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  components: {
    BTable,
    BPagination,
    BFormInput,
    BButton,
    BFormCheckbox,
    flatPickr,
    BFormGroup,
    ValidationProvider,
    ValidationObserver,
    BBadge,
    BFormSelect,
    BFormTextarea,
    BCol,
  },
  data() {
    const options = [
      { value: null, text: "Select Note Type" },
      { value: "note", text: "Note" },
      { value: "customer", text: "Customer" },
      { value: "system", text: "System" },
      { value: "trading", text: "Trading" },
      { value: "other", text: "Other" },
    ];
    return {
      search: "",
      page: 1,
      fields: [
        { key: "type", label: "Type" },
        { key: "date", label: "Date" },
        { key: "dateAmend", label: "Date Amend" },
        { key: "note", label: "Note" },
        { key: "addedBy", label: "Added By (Changed By)" },
        { key: "action", label: "Action" },
      ],
      options,
      notes: [],
      type: options[0].value,
      form: {
        noteId: null,
        type: options[0].value,
        note: "",
        date: "",
      },
      configDate: {
        altFormat: "d-m-Y",
        altInput: true,
        static: true,
      },
      isEdit: false,
    };
  },
  watch: {
    page() {
      this.fetchNotes();
    },
    search() {
      this.fetchNotes();
    },
    type() {
      this.fetchNotes();
    },
    "$route.params.id"() {
      this.fetchNotes();
    },
  },
  created() {
    this.fetchNotes();
  },
  computed: {
    ...mapState("user/detail", ["loadingNotes", "paginationNotes"]),
    paginationSummary() {
      const p = this.paginationNotes || {};
      const page = Number(p.page) || 1;
      const limit = Number(p.limit || p.perPage) || 10;
      const total = Number(p.total) || 0;

      const from = total === 0 ? 0 : (page - 1) * limit + 1;
      const to = total === 0 ? 0 : Math.min(page * limit, total);

      return total === 0
        ? `Showing 0 of 0 entries`
        : `Showing ${from} - ${to} of ${total} entries`;
    },
  },
  methods: {
    ...mapActions("user/detail", ["getNotesByUser", "addNote", "updateNote"]),
    format,
    async fetchNotes() {
      const response = await this.getNotesByUser({
        userId: this.$route.params.id,
        page: this.page,
        ...(this.type ? { type: this.type } : {}),
        ...(this.search ? { search: this.search } : {}),
      });
      if (response) this.notes = response;
    },
    openModal() {
      this.$bvModal.show("add-note-modal");
    },
    closeModal() {
      this.$bvModal.hide("add-note-modal");
      this.reset();
    },
    reset() {
      this.isEdit = false;
      this.form = {
        noteId: null,
        type: this.options[0].value,
        note: "",
        date: "",
      };
    },
    async handleAddNote() {
      const valid = await this.$refs.addNote.validate();
      if (!valid) return;
      const payload = {
        userId: this.$route.params.id,
        note: this.form.note,
        date: this.form.date,
        type: this.form.type,
        ...(this.isEdit ? { noteId: this.form.noteId } : {}),
      };
      const result = this.isEdit
        ? await this.updateNote(payload)
        : await this.addNote(payload);
      if (result) {
        this.closeModal();
        this.isEdit ? (this.notes = result) : this.fetchNotes();
      }
    },
    async openEditNote(item) {
      this.isEdit = true;
      this.form = {
        noteId: item.noteId,
        type: item.type || this.options[0].value,
        note: item.note,
        date: item.date,
      };
      this.$bvModal.show("add-note-modal");
    },
    rowClass(item) {
      if (!item) return "";
      switch (item.type) {
        case "customer":
          return "row-customer";
        case "system":
          return "row-system";
        case "trading":
          return "row-trading";
        default:
          return "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.row-customer {
  background-color: #e9f3ff !important;
}

.row-system {
  background-color: #ffe5e5 !important;
}

.row-trading {
  background-color: #e5ffe5 !important;
}
</style>
